/* ═══════════════════════════════════════════════════════════════════════════
   useTutorWordSet — deterministic "word set of the day" selector for the
   LIVE language tutor (Modo Professor, /language/live).

   PRINCIPLE: the APP owns selection. This module picks ~6–8 words for the
   active learning language using ONLY local rules (mastery + last_practiced),
   reusing the existing useLanguageLearning write/read API. The LLM tutor never
   decides which words to teach — it only DELIVERS the set it is handed and
   SIGNALS success via the [[learned:WORD]] marker (parsed elsewhere).

   Reuses (no new write code):
   - getVocabularyForLanguage (useLanguageLearning.ts:252) — current vocab.
   - addVocabulary           (useLanguageLearning.ts:227)  — seed at mastery 0.
   - refresh                 (useLanguageLearning.ts:114)  — reload after seed.
   Fallback generation reuses the `language-lesson` edge fn (gen + cache only;
   the language_vocabulary INSERT stays client-side, same as LanguageLessonView).
   ═══════════════════════════════════════════════════════════════════════════ */

import { useCallback } from "react";
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import type { LangCode, VocabularyEntry } from "./useLanguageLearning";

// Breadcrumb helper: marks WHY a day-set path was taken/failed so the
// LanguageLive captureMessage("language_tutor.dayset_*") carries the cause.
// Console mirrors it for local dev (invisible on a real iPhone).
function crumb(message: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({ category: "language_tutor", message, level: "info", data });
}

/* ═══ TUNABLES (named, not magic) ═══ */

// Target size of one live-tutor session's active set.
export const DAY_SET_SIZE = 7;
// mirror-chat buckets mastery ≥ 80 as "dominada" (index.ts:1263-1266); a word
// at/above this is excluded from the active set — it's already learned.
export const MASTERY_MASTERED = 80;
// A practiced word becomes "due for review" once it hasn't been touched for
// this many days.
export const REVIEW_DUE_DAYS = 3;
// How much one successful live recall raises mastery (0–100 scale). updateMastery
// clamps, so repeated success walks a word up toward MASTERY_MASTERED.
export const MASTERY_INC = 20;

/* ═══ TYPES ═══ */

export interface TutorWord {
  // language_vocabulary.id — needed to write mastery back. Null only when a
  // freshly generated row could not be re-read for its id (rare; word still
  // teachable, just not scorable this session).
  id: string | null;
  word: string;
  translation: string;
  example?: string;
  mastery: number;
}

// Minimal slice of useLanguageLearning this selector needs. Passing the live
// instance in (rather than calling useLanguageLearning here) keeps a SINGLE
// source of truth — the wrapper's instance also owns the mastery/XP write-back,
// so its state and ours never diverge.
interface LearningApi {
  getVocabularyForLanguage: (lang: LangCode) => VocabularyEntry[];
  addVocabulary: (
    items: Array<{ language: LangCode; word: string; translation: string; lesson_id?: string }>,
  ) => Promise<void>;
  refresh: () => Promise<void>;
}

/* ═══ PURE SELECTION (deterministic, testable) ═══ */

function isDueForReview(entry: VocabularyEntry): boolean {
  if (!entry.last_practiced) return true;
  const ageDays = (Date.now() - new Date(entry.last_practiced).getTime()) / 86_400_000;
  return ageDays >= REVIEW_DUE_DAYS;
}

/**
 * Deterministic ordering: never-practiced first (mastery 0 / last_practiced
 * null, weakest first), then due-for-review (weakest + stalest first).
 * Mastered words (mastery ≥ MASTERY_MASTERED) are excluded entirely.
 */
export function selectDaySet(vocab: VocabularyEntry[], size = DAY_SET_SIZE): VocabularyEntry[] {
  const eligible = vocab.filter((v) => v.mastery < MASTERY_MASTERED);

  const neverPracticed = eligible
    .filter((v) => v.mastery === 0 || v.last_practiced === null)
    .sort((a, b) => a.mastery - b.mastery);

  const dueReview = eligible
    .filter((v) => v.mastery > 0 && v.last_practiced !== null && isDueForReview(v))
    .sort((a, b) => {
      const byMastery = a.mastery - b.mastery;
      if (byMastery !== 0) return byMastery;
      return new Date(a.last_practiced!).getTime() - new Date(b.last_practiced!).getTime();
    });

  const seen = new Set<string>();
  const out: VocabularyEntry[] = [];
  for (const v of [...neverPracticed, ...dueReview]) {
    if (seen.has(v.id)) continue;
    seen.add(v.id);
    out.push(v);
    if (out.length >= size) break;
  }
  return out;
}

/* ═══ FALLBACK GENERATION (no eligible words yet) ═══ */

const TOPIC_COUNT = 3; // language-lesson indexes 1..3 are the always-cached core topics.

async function generateAndSeed(
  learningLang: LangCode,
  nativeLang: LangCode,
  level: string,
  learning: LearningApi,
): Promise<TutorWord[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const lessonIndex = 1 + Math.floor(Math.random() * TOPIC_COUNT);
    const { data, error } = await supabase.functions.invoke("language-lesson", {
      body: {
        native_lang: nativeLang,
        learning_lang: learningLang,
        level,
        lesson_index: lessonIndex,
      },
    });
    if (error || !data || !Array.isArray((data as any).vocabulary)) {
      console.error("[useTutorWordSet] language-lesson generation failed:", error);
      crumb("fallback_lesson_failed", {
        reason: String((error as any)?.message ?? error ?? "no_vocabulary_in_response"),
        learning_lang: learningLang,
      });
      return [];
    }

    const vocab = (data as any).vocabulary as Array<{ word: string; translation: string; example: string }>;
    const slice = vocab.slice(0, DAY_SET_SIZE);
    if (slice.length === 0) {
      crumb("fallback_lesson_empty_vocab", { learning_lang: learningLang });
      return [];
    }

    // Seed at mastery 0 (same path LanguageLessonView uses). onConflict dedupes.
    await learning.addVocabulary(
      slice.map((v) => ({ language: learningLang, word: v.word, translation: v.translation })),
    );

    // addVocabulary doesn't return the rows, so re-read the ids straight from
    // the table — the live tutor needs them to write mastery back.
    const words = slice.map((v) => v.word);
    const { data: rows, error: rereadErr } = await (supabase as any)
      .from("language_vocabulary")
      .select("id, word, mastery")
      .eq("user_id", user.id)
      .eq("language", learningLang)
      .in("word", words);
    if (rereadErr) {
      console.error("[useTutorWordSet] seeded-row re-read failed:", rereadErr);
      crumb("fallback_seed_reread_failed", { reason: String(rereadErr?.message ?? rereadErr) });
    }

    const meta = new Map<string, { id: string; mastery: number }>();
    for (const r of rows || []) meta.set(r.word, { id: r.id, mastery: r.mastery });

    return slice.map((v) => ({
      id: meta.get(v.word)?.id ?? null,
      word: v.word,
      translation: v.translation,
      example: v.example,
      mastery: meta.get(v.word)?.mastery ?? 0,
    }));
  } catch (err) {
    console.error("[useTutorWordSet] generateAndSeed threw:", err);
    crumb("fallback_threw", { reason: String((err as any)?.message ?? err) });
    return [];
  }
}

/* ═══ HOOK ═══ */

export function useTutorWordSet(learning: LearningApi) {
  const buildDaySet = useCallback(
    async (opts: {
      learningLang: LangCode;
      nativeLang: LangCode;
      level: "beginner" | "intermediate" | "advanced";
    }): Promise<TutorWord[]> => {
      const { learningLang, nativeLang, level } = opts;

      // 1. Deterministic pass over what the user already has.
      const ownedVocab = learning.getVocabularyForLanguage(learningLang);
      const chosen = selectDaySet(ownedVocab);
      crumb("dayset_local_selection", {
        learning_lang: learningLang,
        vocab_rows: ownedVocab.length,
        chosen: chosen.length,
      });
      if (chosen.length > 0) {
        return chosen.map((v) => ({
          id: v.id,
          word: v.word,
          translation: v.translation,
          mastery: v.mastery,
        }));
      }

      // 2. No eligible words (new learner, or everything mastered) → generate a
      //    lesson, seed it, and use the generated set directly. refresh() runs
      //    so the rest of the app sees the new rows, but we return the freshly
      //    fetched rows here to avoid a stale-closure read of post-refresh state.
      const generated = await generateAndSeed(learningLang, nativeLang, level, learning);
      void learning.refresh();
      return generated.slice(0, DAY_SET_SIZE);
    },
    [learning],
  );

  return { buildDaySet };
}
